import { translate, translateWithVars } from "shared/languages";
import { LoyaltyRewardType } from "shared/types/enum";
import { formatCurrency } from "shared/utils";

// "1 Free Class" / "2 Free Classes". Indonesian has no plural, so both keys
// read "Kelas Gratis" there. A separate key rather than i18next's plural
// machinery, which would need a `count` option the translate() cast can't pass.
const freeCredits = (qty: number) =>
  `${qty} ${translate(qty === 1 ? "loyalty.reward.credit_unit_single" : "loyalty.reward.credit_unit")}`;

// Human reward label, e.g. "2 Kelas Gratis" / "Diskon 50%" / "Diskon Rp 150.000".
// Works for anything payload-shaped: vouchers, cycle milestones, config rows.
export const rewardLabel = (v: {
  type?: string;
  reward_type?: string;
  credit_qty: number | null;
  discount_mode: string | null;
  discount_value: number | null;
}): string => {
  const type = v.type ?? v.reward_type;
  if (type === LoyaltyRewardType.FreeCredit) {
    return freeCredits(v.credit_qty ?? 0);
  }
  const amount =
    v.discount_mode === "percent"
      ? `${v.discount_value ?? 0}%`
      : formatCurrency(v.discount_value ?? 0);
  return `${translate("loyalty.reward.discount")} ${amount}`;
};

// A discount code's rule, e.g. "15%", "15% (maks. Rp 50.000)", "Rp 50.000".
// The maximum only ever clamps a percentage (the server ignores it on a fixed
// amount), so it is shown only there. Used by Performa Kode Diskon's table and
// its PDF.
export const discountCodeRuleLabel = (c: {
  discount_mode: string;
  discount_value: number;
  max_discount_amount: number | null;
}): string => {
  if (c.discount_mode !== "percent") return formatCurrency(c.discount_value);
  return c.max_discount_amount
    ? `${c.discount_value}% (${translateWithVars("analyticsPage.discountCode.max_discount", {
        amount: formatCurrency(c.max_discount_amount),
      })})`
    : `${c.discount_value}%`;
};

// Which reward a Pemakaian Reward row came from (the voucher's source).
// "referral" alone is a referral credit whose voucher could not be found: the
// programme is known, the side is not.
export const rewardProgramLabel = (program: string): string =>
  translate(
    program === "loyalty_milestone"
      ? "analyticsPage.rewardUsage.program_loyalty"
      : program === "referral_referrer"
        ? "analyticsPage.rewardUsage.program_referrer"
        : program === "referral_friend"
          ? "analyticsPage.rewardUsage.program_friend"
          : "analyticsPage.rewardUsage.program_referral",
  );

// The server's referral reward summaries (friend_offer, reward_summary) are
// Indonesian prose in exactly the three shapes its rewardSummaryText
// produces: "N kelas gratis" (older servers: "N kredit gratis") / "Diskon 20%" /
// "Diskon Rp 50.000". Re-render
// them in the active language instead of showing Indonesian inside the
// English UI; anything unrecognized passes through untouched.
export const localizeRewardSummary = (text: string | null): string | null => {
  if (!text) return text;
  const credit = text.match(/^(\d+) (?:kelas|kredit) gratis$/i);
  if (credit) return freeCredits(Number(credit[1]));
  const discount = text.match(/^Diskon (.+)$/i);
  if (discount) return `${translate("loyalty.reward.discount")} ${discount[1]}`;
  return text;
};

/**
 * The system void shape written by the server when a stamp or voucher is
 * revoked automatically rather than by an admin. Kept in sync with
 * STAMP_REVERTED_VOID_REASON in spott-server/src/services/loyalty.ts.
 * "booking_reverted" is accepted too: the schema comment names it, and an
 * older row may carry it.
 */
const SYSTEM_VOID_REASONS = ["stamp_reverted", "booking_reverted"];

export interface AuditReason {
  /** The reason text to show. Null when there is nothing to record. */
  text: string | null;
  /** Who caused it: an admin's name, or null for an automatic revocation. */
  actor: string | null;
  /** True when the server revoked this by itself (no admin typed a reason). */
  system: boolean;
}

/**
 * The audit trail for one stamp: why it was added by hand, or why it was taken
 * away. A stamp revoked because its booking stopped being Completed carries no
 * admin reason at all, so it gets a localized explanation instead of the raw
 * "stamp_reverted" token.
 *
 * Removal wins over addition: a manually-added stamp that was later revoked
 * should read as the revocation, which is the newer fact.
 */
export const stampReason = (s: {
  source: string;
  reason: string | null;
  voided: boolean;
  void_reason: string | null;
  actor_name: string | null;
  voided_by_name: string | null;
}): AuditReason => {
  if (s.voided) {
    const system =
      !s.voided_by_name || SYSTEM_VOID_REASONS.includes(s.void_reason ?? "");
    return {
      text: system
        ? translate("loyalty.reason.booking_reverted")
        : s.void_reason,
      actor: system ? null : s.voided_by_name,
      system,
    };
  }
  // A booking-sourced stamp that still counts has no reason to record.
  if (!s.reason) return { text: null, actor: null, system: false };
  return { text: s.reason, actor: s.actor_name, system: false };
};

/** The audit trail for one voucher: why an admin invalidated it. */
export const voucherReason = (v: {
  void_reason: string | null;
  voided_by_name: string | null;
  rolled_back_by?: { name: string | null; reason: string | null } | null;
}): AuditReason => {
  if (!v.void_reason) return { text: null, actor: null, system: false };
  // Voided by an admin while deleting the sale it was spent on: an admin
  // action (named), with a fixed reason the server stores as a token.
  if (v.void_reason === "sale_voided") {
    return {
      text: translate("loyalty.reason.sale_voided"),
      actor: v.voided_by_name,
      system: false,
    };
  }
  // Voided by an admin while voiding the sale that CONVERTED the referral
  // this reward came from (the referrer's unspent reward, or the friend's
  // intro voucher redeemed on that sale). Same shape: named admin, fixed
  // server token, its own sentence.
  if (v.void_reason === "referral_sale_voided") {
    return {
      text: translate("loyalty.reason.referral_sale_voided"),
      actor: v.voided_by_name,
      system: false,
    };
  }
  const system =
    !v.voided_by_name || SYSTEM_VOID_REASONS.includes(v.void_reason);
  // A system-shaped void that an ADMIN caused by rolling the card back: the
  // server pairs it with the stamp that carries the admin's name and reason
  // (System Review R13), so the row names them instead of "auto-revoked".
  if (system && v.rolled_back_by) {
    return {
      text: translateWithVars("loyalty.reason.rolled_back_by", {
        name: v.rolled_back_by.name ?? "-",
        reason: v.rolled_back_by.reason ?? "-",
      }),
      actor: null,
      system: false,
    };
  }
  return {
    text: system ? translate("loyalty.reason.stamp_reverted") : v.void_reason,
    actor: system ? null : v.voided_by_name,
    system,
  };
};

/**
 * A referral row's friend discount, when it was spent on a sale OTHER than
 * the one that converted the referral: "Dipakai untuk <product>". No date:
 * the product points at the sale, and Pemakaian Reward has the date.
 * Without it the row shows the converting sale at full price and reads as
 * the reward missing (System Review R20). Null when the discount went on the
 * converting sale itself, was never used, or the reward was a free credit.
 * Shared by the Performa Referral page and its PDF.
 */
export const friendDiscountElsewhere = (row: {
  friend_discount_used_on?: {
    package_name: string;
    purchased_at: string;
    on_conversion: boolean;
  } | null;
}): string | null =>
  row.friend_discount_used_on && !row.friend_discount_used_on.on_conversion
    ? translateWithVars("analyticsPage.referral.friend_discount_used_on", {
        product: row.friend_discount_used_on.package_name,
      })
    : null;
